import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { Course, Price } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { DollarSign, Save } from 'lucide-react'

interface CourseWithPrices extends Course {
  standard_price?: Price
  retry_price?: Price
}

export function PricingManagePage() {
  useDocumentTitle('価格設定')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTenant, memberships } = useTenant()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [courses, setCourses] = useState<CourseWithPrices[]>([])

  useEffect(() => {
    const isTenantAdmin = memberships.some(
      (m) => m.tenant_id === currentTenant?.tenant_id && m.role_in_tenant === 'tenant_admin'
    )

    if (!isTenantAdmin && memberships.length > 0) {
      navigate('/mypage', { replace: true })
      return
    }

    if (user && currentTenant) {
      fetchCoursesWithPrices()
    }
  }, [user, currentTenant])

  const fetchCoursesWithPrices = async () => {
    if (!currentTenant) return

    try {
      setLoading(true)
      setError(null)

      // 講座一覧を取得
      const { data: coursesData, error: coursesError } = await supabase
        .from('courses')
        .select('*')
        .eq('tenant_id', currentTenant.tenant_id)
        .eq('status', 'published')

      if (coursesError) throw coursesError

      // 各講座の価格を取得
      const coursesWithPrices: CourseWithPrices[] = []
      for (const course of coursesData || []) {
        const { data: pricesData } = await supabase
          .from('prices')
          .select('*')
          .eq('course_id', course.course_id)

        const standardPrice = pricesData?.find((p) => p.price_type === 'standard')
        const retryPrice = pricesData?.find((p) => p.price_type === 'retry')

        coursesWithPrices.push({
          ...course,
          standard_price: standardPrice,
          retry_price: retryPrice,
        })
      }

      setCourses(coursesWithPrices)
    } catch (err: any) {
      console.error('Error fetching courses with prices:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSavePrice = async (
    courseId: string,
    priceType: 'standard' | 'retry',
    amount: number,
    stripePriceId: string
  ) => {
    if (!currentTenant) return

    try {
      setSaving(true)
      setError(null)

      const priceData = {
        tenant_id: currentTenant.tenant_id,
        course_id: courseId,
        price_type: priceType,
        amount,
        stripe_price_id: stripePriceId || null,
        currency: 'JPY',
      }

      // 既存の価格を確認
      const course = courses.find((c) => c.course_id === courseId)
      const existingPrice =
        priceType === 'standard' ? course?.standard_price : course?.retry_price

      if (existingPrice) {
        // 更新
        const { error: updateError } = await supabase
          .from('prices')
          .update(priceData)
          .eq('price_id', existingPrice.price_id)

        if (updateError) throw updateError
      } else {
        // 新規作成
        const { error: insertError } = await supabase.from('prices').insert([priceData])

        if (insertError) throw insertError
      }

      fetchCoursesWithPrices()
    } catch (err: any) {
      console.error('Error saving price:', err)
      setError(err.message || '価格の保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="価格設定を読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">価格設定</h1>
          <p className="mt-2 text-gray-600">講座の受講料と再受験料を設定します</p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        {courses.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">公開中の講座がありません</h3>
            <p className="text-gray-600">講座を公開してから価格を設定してください</p>
          </div>
        ) : (
          <div className="space-y-6">
            {courses.map((course) => (
              <div key={course.course_id} className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{course.title}</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 通常価格 */}
                  <PriceForm
                    title="通常受講料"
                    priceType="standard"
                    price={course.standard_price}
                    onSave={(amount, stripePriceId) =>
                      handleSavePrice(course.course_id, 'standard', amount, stripePriceId)
                    }
                    saving={saving}
                  />

                  {/* 再受験価格 */}
                  <PriceForm
                    title="再受験料"
                    priceType="retry"
                    price={course.retry_price}
                    onSave={(amount, stripePriceId) =>
                      handleSavePrice(course.course_id, 'retry', amount, stripePriceId)
                    }
                    saving={saving}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  )
}

interface PriceFormProps {
  title: string
  priceType: 'standard' | 'retry'
  price?: Price
  onSave: (amount: number, stripePriceId: string) => void
  saving: boolean
}

function PriceForm({ title, priceType: _priceType, price, onSave, saving }: PriceFormProps) {
  const [amount, setAmount] = useState(price?.amount || 0)
  const [stripePriceId, setStripePriceId] = useState(price?.stripe_price_id || '')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(amount, stripePriceId)
  }

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-4">
      <h3 className="font-medium text-gray-900 mb-4">{title}</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            金額（円） <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            min="0"
            required
            value={amount}
            onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="10000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stripe Price ID <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            required
            value={stripePriceId}
            onChange={(e) => setStripePriceId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder="price_xxxxxxxxxxxxx"
          />
          <p className="mt-1 text-xs text-gray-500">
            Stripeダッシュボードで作成した価格IDを入力してください
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
        >
          <Save className="h-4 w-4" />
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </form>
  )
}
